import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';

interface MoreMenuProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

export function MoreMenu({ open, onClose, children }: MoreMenuProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/20 md:hidden" aria-hidden="true" />
      {/* Menu panel */}
      <div
        ref={ref}
        className={cn(
          'bg-surface border-border fixed bottom-16 z-50 max-h-64 w-full overflow-y-auto rounded-t-xl border p-2 shadow-lg md:hidden',
          'animate-slide-up',
        )}
        role="menu"
      >
        <p className="text-ink-muted px-3 py-1.5 text-xs font-medium uppercase tracking-wide">
          {t('nav.more')}
        </p>
        {children}
      </div>
    </>
  );
}
